import React, { useState, useEffect, useRef } from 'react'
import { Box, Typography, Portal, TextField } from '@mui/material'
import { XMarkIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useUser } from '../../../hooks/useUser'
import { useDemographicAnswers } from '../../../apiLayer/queryHooks/queries'
import {
  useSaveDemographicAnswer,
  useDeleteDemographicAnswer
} from '../../../apiLayer/queryHooks/useDemographicMutations'
import { DemographicChoiceButton } from './DemographicQuestionsModal/DemographicChoiceButton'
import { DemographicCountrySelect } from './DemographicQuestionsModal/DemographicCountrySelect'
import { DemographicQuestionCard } from './DemographicQuestionsModal/DemographicQuestionCard'
import {
  DEMOGRAPHIC_QUESTIONS,
  IC_ROLE_VALUE,
  IC_TEAM_SIZE_VALUE,
  OTHER_ROLE_VALUE
} from '../../../config/demographicVariables'
import { DemographicAnswers, DemographicQuestionId } from '../../../../../pure-js/types/Demographics'
import { MODAL_Z_INDEX } from '../../../shared/components/DefaultModal'

export interface DemographicModalAnswers {
  answers: Partial<Record<DemographicQuestionId, number | null>>
  roleLevelOtherText?: string
}

interface DemographicQuestionsModalProps {
  isOpen: boolean
  /** Skip — close without persisting Demographics chapter */
  onSkip: () => void
  /** Continue — persist Demographics chapter then open assessment */
  onContinue: (answers: DemographicModalAnswers) => void
}

const PAGE_1_IDS: DemographicQuestionId[] = [
  'demographic_age',
  'demographic_gender',
  'demographic_country_of_residence',
  'demographic_gaming_experience'
]

const PAGE_2_IDS: DemographicQuestionId[] = [
  'demographic_role_level',
  'demographic_team_size',
  'demographic_years_experience'
]

/**
 * DemographicQuestionsModal — Step 1 of 2 in the assessment flow.
 * All questions are optional. Skip closes without saving progress;
 * Continue marks Demographics as complete.
 * Q4 = IC (value 1) → auto-saves team_size = 0 and hides Q5.
 * Internal pagination: page 1 (age/gender/country/gaming), page 2 (role/team/experience).
 */
export const DemographicQuestionsModal: React.FC<DemographicQuestionsModalProps> = ({ isOpen, onSkip, onContinue }) => {
  const { t } = useTranslation()
  const { data: user } = useUser()
  const userId = user?.id || ''
  const { data: demographicAnswersResponse, isFetched: demographicAnswersFetched } = useDemographicAnswers(userId, {
    enabled: isOpen && !!userId
  })

  const saveMutation = useSaveDemographicAnswer()
  const deleteMutation = useDeleteDemographicAnswer()

  const [localAnswers, setLocalAnswers] = useState<Partial<Record<DemographicQuestionId, number | null>>>({})
  const [roleLevelOtherText, setRoleLevelOtherText] = useState('')
  const hasHydratedForOpenRef = useRef(false)
  const [currentPage, setCurrentPage] = useState<1 | 2>(1)

  useEffect(() => {
    if (!isOpen) {
      setCurrentPage(1)
      setLocalAnswers({})
      setRoleLevelOtherText('')
      hasHydratedForOpenRef.current = false
      return
    }
    if (!userId || hasHydratedForOpenRef.current) {
      return
    }
    const fromCollection = demographicAnswersResponse?.data as DemographicAnswers | undefined
    const fromUserDoc = user?.demographics as DemographicAnswers | undefined
    const answers =
      fromCollection && Object.keys(fromCollection).length > 0
        ? fromCollection
        : fromUserDoc && Object.keys(fromUserDoc).length > 0
          ? fromUserDoc
          : undefined
    if (!demographicAnswersFetched && !fromUserDoc) {
      return
    }
    hasHydratedForOpenRef.current = true
    if (answers) {
      const hydrated = Object.fromEntries(
        Object.entries(answers).map(([k, v]) => [k as DemographicQuestionId, v.answer])
      ) as Partial<Record<DemographicQuestionId, number | null>>
      setLocalAnswers(hydrated)
      setRoleLevelOtherText(answers.demographic_role_level?.otherText ?? '')
    }
  }, [isOpen, userId, demographicAnswersResponse?.data, demographicAnswersFetched, user?.demographics])

  const getAnswer = (questionId: DemographicQuestionId): number | null => localAnswers[questionId] ?? null

  const isIC = getAnswer('demographic_role_level') === IC_ROLE_VALUE
  const isOtherRole = getAnswer('demographic_role_level') === OTHER_ROLE_VALUE

  const saveAnswer = async (params: {
    userId: string
    questionId: DemographicQuestionId
    answer: number
    otherText?: string | null
  }) => {
    try {
      await saveMutation.mutateAsync(params)
    } catch (_error) {
      // Keep the flow responsive even if demographic persistence fails.
    }
  }

  const deleteAnswer = async (params: { userId: string; questionId: DemographicQuestionId }) => {
    try {
      await deleteMutation.mutateAsync(params)
    } catch (_error) {
      // Keep the flow responsive even if demographic persistence fails.
    }
  }

  const handleChoiceSelect = async (questionId: DemographicQuestionId, value: number) => {
    const current = getAnswer(questionId)

    if (current === value) {
      setLocalAnswers((prev) => ({ ...prev, [questionId]: null }))
      if (questionId === 'demographic_role_level') {
        setRoleLevelOtherText('')
        if (value === IC_ROLE_VALUE) {
          setLocalAnswers((prev) => ({ ...prev, demographic_team_size: null }))
          if (userId) await deleteAnswer({ userId, questionId: 'demographic_team_size' })
        }
      }
      if (userId) await deleteAnswer({ userId, questionId })
      return
    }

    setLocalAnswers((prev) => ({ ...prev, [questionId]: value }))
    if (questionId === 'demographic_role_level' && value !== OTHER_ROLE_VALUE) {
      setRoleLevelOtherText('')
    }
    if (userId) {
      await saveAnswer({
        userId,
        questionId,
        answer: value,
        ...(questionId === 'demographic_role_level'
          ? { otherText: value === OTHER_ROLE_VALUE ? roleLevelOtherText.trim() || null : null }
          : {})
      })
    }

    if (questionId === 'demographic_role_level') {
      if (value === IC_ROLE_VALUE) {
        setLocalAnswers((prev) => ({ ...prev, demographic_team_size: IC_TEAM_SIZE_VALUE }))
        if (userId) await saveAnswer({ userId, questionId: 'demographic_team_size', answer: IC_TEAM_SIZE_VALUE })
      } else if (current === IC_ROLE_VALUE) {
        setLocalAnswers((prev) => ({ ...prev, demographic_team_size: null }))
        if (userId) await deleteAnswer({ userId, questionId: 'demographic_team_size' })
      }
    }
  }

  const handleRoleLevelOtherTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRoleLevelOtherText(event.target.value)
  }

  const handleCountryChange = async (value: number | null) => {
    setLocalAnswers((prev) => ({ ...prev, demographic_country_of_residence: value }))
    if (!userId) return
    if (value === null) {
      await deleteAnswer({ userId, questionId: 'demographic_country_of_residence' })
    } else {
      await saveAnswer({ userId, questionId: 'demographic_country_of_residence', answer: value })
    }
  }

  const handleContinue = async () => {
    if (currentPage === 1) {
      setCurrentPage(2)
    } else {
      const trimmedRoleLevelOtherText = roleLevelOtherText.trim()

      if (isOtherRole && userId) {
        void saveAnswer({
          userId,
          questionId: 'demographic_role_level',
          answer: OTHER_ROLE_VALUE,
          otherText: trimmedRoleLevelOtherText.length > 0 ? trimmedRoleLevelOtherText : null
        })
      }

      onContinue({
        answers: localAnswers,
        roleLevelOtherText: isOtherRole && trimmedRoleLevelOtherText.length > 0 ? trimmedRoleLevelOtherText : undefined
      })
    }
  }

  if (!isOpen) return null

  const pageIds = currentPage === 1 ? PAGE_1_IDS : PAGE_2_IDS
  const pageQuestions = DEMOGRAPHIC_QUESTIONS.filter((q) => pageIds.includes(q.id))

  return (
    <Portal>
      {/* Backdrop */}
      <Box
        onClick={onSkip}
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          zIndex: MODAL_Z_INDEX,
          overflowY: 'auto',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          py: { xs: 2, sm: 4 },
          px: { xs: 1, sm: 2 }
        }}
      >
        {/* Modal panel — stops propagation so clicking inside doesn't close */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: '100%',
            maxWidth: '860px',
            backgroundColor: INTERAX_COLORS.offBlack,
            borderRadius: '16px',
            p: { xs: 2.5, sm: 3.5 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5
          }}
        >
          {/* Header row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {currentPage === 2 && (
                <Box
                  onClick={() => setCurrentPage(1)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: INTERAX_COLORS.thunderGrey,
                    '&:hover': { color: INTERAX_COLORS.white },
                    flexShrink: 0
                  }}
                >
                  <ChevronLeftIcon style={{ width: 18, height: 18 }} />
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography
                  sx={{
                    ...INTERAX_TYPOGRAPHY.body3,
                    color: INTERAX_COLORS.thunderGrey,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontWeight: 600,
                    fontSize: '11px'
                  }}
                >
                  {t('gamer.demographics.stepLabel')}
                </Typography>
                <Box sx={{ width: 160, height: 3, backgroundColor: INTERAX_COLORS.thunderGrey20, borderRadius: 2 }}>
                  <Box
                    sx={{
                      width: currentPage === 1 ? '50%' : '100%',
                      height: '100%',
                      backgroundColor: INTERAX_COLORS.sun,
                      borderRadius: 2
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              onClick={onSkip}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: INTERAX_COLORS.thunderGrey,
                '&:hover': { color: INTERAX_COLORS.white },
                flexShrink: 0
              }}
            >
              <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, fontWeight: 500 }}>
                {t('gamer.demographics.skip')}
              </Typography>
              <XMarkIcon style={{ width: 14, height: 14 }} />
            </Box>
          </Box>

          {/* Title */}
          <Box>
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.headline4,
                color: INTERAX_COLORS.white,
                fontWeight: 700,
                mb: 0.5
              }}
            >
              {t('gamer.demographics.title')}
            </Typography>
            <Typography sx={{ ...INTERAX_TYPOGRAPHY.body3, color: INTERAX_COLORS.thunderGrey }}>
              {t('gamer.demographics.subtitle')}
            </Typography>
          </Box>

          {/* Questions grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1.5,
              alignItems: 'stretch'
            }}
          >
            {(() => {
              const pageOffset = currentPage === 1 ? 0 : PAGE_1_IDS.length
              let visibleIndex = 0
              return pageQuestions.map((question) => {
                const roleNotSelected = getAnswer('demographic_role_level') === null
                const hideTeamSize = question.id === 'demographic_team_size' && (isIC || roleNotSelected)
                const hideYearsExperience = question.id === 'demographic_years_experience' && roleNotSelected
                if (hideTeamSize || hideYearsExperience) return null
                visibleIndex += 1
                const questionNumber = pageOffset + visibleIndex

                if (question.type === 'country') {
                  return (
                    <DemographicQuestionCard
                      key={question.id}
                      questionNumber={questionNumber}
                      title={t(question.titleKey)}
                      tooltip={t(question.tooltipKey)}
                    >
                      <Box sx={{ width: '100%' }}>
                        <DemographicCountrySelect
                          value={getAnswer('demographic_country_of_residence')}
                          onChange={handleCountryChange}
                        />
                      </Box>
                    </DemographicQuestionCard>
                  )
                }

                return (
                  <DemographicQuestionCard
                    key={question.id}
                    questionNumber={questionNumber}
                    title={t(question.titleKey)}
                    tooltip={t(question.tooltipKey)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: question.verticalOptions ? 'column' : 'row',
                        flexWrap: question.verticalOptions ? 'nowrap' : 'wrap',
                        gap: 1,
                        width: '100%'
                      }}
                    >
                      {question.options?.map((option) => (
                        <DemographicChoiceButton
                          key={option.value}
                          label={t(option.labelKey)}
                          tooltip={option.hoverKey ? t(option.hoverKey) : undefined}
                          selected={getAnswer(question.id) === option.value}
                          onClick={() => handleChoiceSelect(question.id, option.value)}
                        />
                      ))}
                    </Box>
                    {question.id === 'demographic_role_level' && isOtherRole && (
                      <TextField
                        fullWidth
                        value={roleLevelOtherText}
                        onChange={handleRoleLevelOtherTextChange}
                        placeholder={t('gamer.demographics.questions.roleLevel.otherPlaceholder')}
                        inputProps={{
                          'aria-label': t('gamer.demographics.questions.roleLevel.otherPlaceholder'),
                          maxLength: 120
                        }}
                        sx={{
                          mt: 1.5,
                          '& .MuiInputBase-root': {
                            color: INTERAX_COLORS.white,
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            borderRadius: '12px'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.15)'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: INTERAX_COLORS.sun40
                          },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: INTERAX_COLORS.sun
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: INTERAX_COLORS.thunderGrey,
                            opacity: 1
                          }
                        }}
                      />
                    )}
                  </DemographicQuestionCard>
                )
              })
            })()}
          </Box>

          {/* Continue button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Box
              onClick={handleContinue}
              sx={{
                px: 4,
                py: 1.25,
                borderRadius: '999px',
                backgroundColor: INTERAX_COLORS.sun,
                cursor: 'pointer',
                transition: 'all 0.15s ease-in-out',
                '&:hover': { opacity: 0.9, transform: 'translateY(-1px)' }
              }}
            >
              <Typography
                sx={{
                  ...INTERAX_TYPOGRAPHY.body2,
                  color: INTERAX_COLORS.offBlack,
                  fontWeight: 700
                }}
              >
                {t('gamer.demographics.continue')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Portal>
  )
}
